import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@shared/ui/Button'
import { useCodeInput } from './useCodeInput'
import { useResendCountdown, saveNextResend, readNextResend } from '../../model/useResendTimer'
import styles from './CodeStep.module.css'

const CODE_LENGTH = 6

interface CodeStepProps {
  /** Email, на который отправлен код (для подзаголовка) */
  email: string
  /** Обработчик полного кода (после автозаполнения) */
  onSubmit: (code: string) => Promise<void>
  /** Запрос повторной отправки кода */
  onResend: () => Promise<void>
  /** Возврат к шагу ввода email */
  onChangeEmail: () => void
  /** Серверная ошибка */
  errorText?: string | null
  /** Оставшееся число попыток (для отображения) */
  attemptsLeft?: number | null
  /** Заблокировать ввод (лимит попыток / истёкший код) */
  isLocked?: boolean
  /** ISO-время, когда можно снова нажать "Отправить повторно" */
  nextResendAt?: string | null
  /** Идёт ли проверка кода */
  isSubmitting?: boolean
  /** Триггер shake-анимации + очистки полей */
  shakeSignal?: number
}

/**
 * Шаг ввода 6-значного кода из email: отдельные ячейки с paste, автопереходом,
 * автоотправкой по заполнении; shake и очистка при неверном коде; таймер resend.
 * @param email - Email пользователя для подзаголовка
 * @param onSubmit - Колбэк полного кода
 * @param onResend - Запрос повторной отправки
 * @param onChangeEmail - Возврат к предыдущему шагу
 * @param errorText - Серверная ошибка
 * @param attemptsLeft - Оставшееся число попыток
 * @param isLocked - Заблокировать поля
 * @param nextResendAt - ISO, когда можно запросить повторно
 * @param isSubmitting - Идёт ли отправка
 * @param shakeSignal - Триггер shake-анимации
 */
export const CodeStep = ({
  email,
  onSubmit,
  onResend,
  onChangeEmail,
  errorText = null,
  attemptsLeft = null,
  isLocked = false,
  nextResendAt = null,
  isSubmitting = false,
  shakeSignal = 0
}: CodeStepProps) => {
  const { digits, inputsRef, shakeControls, handleChange, handleKeyDown, handlePaste } =
    useCodeInput({ length: CODE_LENGTH, onComplete: onSubmit, shakeSignal })

  const effectiveResendAt = nextResendAt ?? readNextResend()
  const resendIn = useResendCountdown(effectiveResendAt)

  useEffect(() => {
    if (nextResendAt) saveNextResend(nextResendAt)
  }, [nextResendAt])

  const canResend = resendIn === 0 && !isSubmitting
  const cellClass = [styles.cell, errorText && styles.invalid].filter(Boolean).join(' ')

  return (
    <div className={styles.step}>
      <h1 className={styles.title}>Введите код</h1>
      <p className={styles.subtitle}>Код отправлен на {email}</p>

      <motion.div animate={shakeControls} className={styles.cells}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { inputsRef.current[i] = el }}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            inputMode="numeric"
            maxLength={1}
            disabled={isLocked || isSubmitting}
            className={cellClass}
          />
        ))}
      </motion.div>

      {errorText && (
        <div className={styles.error}>
          {errorText}
          {attemptsLeft !== null && attemptsLeft > 0 && ` Осталось попыток: ${attemptsLeft}`}
        </div>
      )}

      <Button variant="secondary" onClick={() => void onResend()} disabled={!canResend} block>
        {resendIn > 0 ? `Отправить повторно через ${resendIn}с` : 'Отправить код повторно'}
      </Button>

      <Button variant="link" onClick={onChangeEmail} block>
        Изменить email
      </Button>
    </div>
  )
}
